import { useDispatch, useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { TrackingEventConstants } from '../../../constants/tracking'
import { Format } from '../..'
import QuestionSingle from '../Questions/Single'
import OverlayPanel from '../../shared/Routing/OverlayPanel'
import { ReverseMortgagePanel } from './ReverseMortgagePanel'
import history from '../../../history'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { CUSTOMER_MESSAGES } from '../../../constants/customerMessages'
import { selectEquityPercent } from '../../../store/selectors/home/reverseMortgage'
import { useTrackingDebounceSetters } from '../../../hooks/tracking'
import { useTrackingContext } from '../../../providers/tracking'
import { RootState } from '../../../types/rootState'
import { Waypoint } from 'react-waypoint'
import { HOMEOWNER_DIGEST_REVERSE_MORTGAGE_MODULE } from '../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import {
  Card,
  CardHeader,
  CardFooter,
  Heading,
  CardBody,
  TrackScrolledIntoView,
  Text,
  Button
} from '@homebotapp/hb-react-component-catalog'
import { ClientEventName } from '../../../api/gqlaxy/gql/generated/graphql'
import { useCreateHomeClientEvent } from '../../../hooks/useCreateHomeClientEvent'

import styles from './ReverseMortgageModule.module.scss'
import { REVERSE_MORTGAGE } from '../../../constants/document_ids'
import { act } from '@testing-library/react'

export const messages = defineMessages({
  title: {
    id: 'Homeowner.reverseMortgages.heading',
    defaultMessage: 'Retirement on the horizon?'
  },
  estimatedEquity: {
    id: 'Homeowner.reverseMortgages.subHeading',
    defaultMessage: 'Your estimated home equity is:'
  },
  description: {
    id: 'Homeowner.reverseMortgages.description',
    defaultMessage:
      'This means you could possibly get a reverse mortgage, which might help you leverage home equity and income'
  },
  learnMoreButton: {
    id: 'Homeowner.reverseMortgages.expandButton',
    defaultMessage: 'Learn more about this'
  }
})

export const ReverseMortgageModule = () => {
  const intl = useIntl()

  const { trackingClient } = useTrackingContext()

  const equityPercent = useSelector<RootState, boolean>(selectEquityPercent) || 0

  const createReverseMortgageClientEvent = useCreateHomeClientEvent({
    eventName: ClientEventName.ViewedReverseMortgagePanel
  })

  const handleReverseMortgagePanelOpen = () => {
    createReverseMortgageClientEvent()
    history.push(OVERLAY_HELPERS.reverseMortgage.buildPath())
  }

  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        module_name: TrackingEventConstants.REVERSE_MORTGAGE
      })
  )

  return (
    <TrackScrolledIntoView
      tracking={{
        guid: '6aeaennGdZXv98KhhhqTcN',
        ui_context: HOMEOWNER_DIGEST_REVERSE_MORTGAGE_MODULE,
        descriptor: 'Homeowner Digest Reverse Mortgage Module'
      }}
    >
      <Card
        data-qa='ReverseMortgageCard'
        size='lg'
        id={REVERSE_MORTGAGE}
        scrollMarginTop='var(--digest-module-scroll-margin-top)'
      >
        <CardHeader data-qa='ReverseMortgageHeadline' className={styles.cardHeader}>
          <Heading fontWeight='light' size='md' marginBottom={0}>
            {intl.formatMessage(messages.title)}
          </Heading>
          <Heading fontWeight='light' size='md' marginBottom={0}>
            {intl.formatMessage(messages.estimatedEquity)}
          </Heading>
        </CardHeader>
        <CardBody data-qa='ReverseMortgageCardBody' className={styles.cardBody}>
          <Text size='3xl' color='success' fontWeight={2}>
            <Format.Percentage value={equityPercent} format='0.0' />
          </Text>
          <Waypoint onEnter={trackModuleViewed} bottomOffset='50%'>
            <div />
          </Waypoint>
          <Text size='sm' marginBottom='6'>
            {intl.formatMessage(messages.description)}
          </Text>
          <Button
            data-qa='reverse-mortgages-details-button'
            tracking={{
              guid: 'drcFCiiXwnCCB7haSYDxZ4',
              ui_context: HOMEOWNER_DIGEST_REVERSE_MORTGAGE_MODULE,
              descriptor: messages.learnMoreButton.defaultMessage
            }}
            onClick={handleReverseMortgagePanelOpen}
          >
            {intl.formatMessage(messages.learnMoreButton)}
          </Button>
        </CardBody>
        <CardFooter backgroundColor='neutral.100' className={styles.cardFooter}>
          <QuestionSingle
            topic={CUSTOMER_MESSAGES.REVERSE_MORTGAGE.topic}
            topicKey={CUSTOMER_MESSAGES.REVERSE_MORTGAGE.topicKey}
            question={CUSTOMER_MESSAGES.REVERSE_MORTGAGE.message}
          />
        </CardFooter>
        <OverlayPanel
          back={OVERLAY_HELPERS.reverseMortgage.getOrigin()}
          path={OVERLAY_HELPERS.reverseMortgage.buildPath()}
          title={OVERLAY_HELPERS.getTitle('reverseMortgage', intl)}
          component={ReverseMortgagePanel}
        />
      </Card>
    </TrackScrolledIntoView>
  )
}
